/**
 * DebugSuiteContainer — Netcode + Debug Hub
 *
 * Two-column layout (2026-05-19 redesign per DJ ask: "make our prior
 * scroll bar turn on widget thing the left side of our shift D then a
 * main center window with all the debug and stuff"):
 *   - LEFT (180px, scrollable independently): widget toggle list
 *     grouped by category (Netcode / Frame & Perf / Debug / Tools). One
 *     row per widget; click toggles whether that widget is expanded in
 *     the right pane. Hub selection is deliberately independent from
 *     floating-window selection.
 *   - RIGHT (rest, scrollable independently): the currently-toggled
 *     widgets' content stacked vertically.
 *
 * Reuses CONTENT_BY_ID from content.tsx — no rewriting tool content.
 * Each section mounts its content component only when toggled on, so
 * the hub has near-zero runtime cost when nothing's expanded.
 *
 * Reads the hub-specific selection map from WindowManagerContext. Floating
 * cards remain an explicit action in WindowLauncher.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ToolWindow, type ToolWindowHandle } from '../components/ToolWindow';
import { CONTENT_BY_ID } from '../framework/content';
import { useWindows } from '../framework/WindowManagerContext';
import { CATEGORY_META, type WindowCategory, type WindowDef, type WindowId } from '../framework/types';
import { DEBUG_HUB_PRESETS } from '../../../config/debug/debugPresets';
import { storage } from '../../services/StorageService';
import {
  buildDebugSuiteShareUrl,
  clampDebugSuiteScale,
  DEBUG_SUITE_LAYOUT_OPTIONS,
  DEBUG_SUITE_SCALE_STEP,
  DEBUG_SUITE_VIEW_STORAGE_KEY,
  isDebugSuiteLayoutId,
  readDebugSuiteViewFromUrl,
  resolveDebugSuiteLayout,
  type DebugSuiteGeometry,
  type DebugSuiteLayoutId,
  type DebugSuiteViewState,
} from './debugSuiteLayout';
import { bootHref } from '../../../config/boot/bootUrl';

// Derived, not hand-listed: CATEGORY_META's insertion order is the sidebar
// section order. The old hand-written list silently omitted the 'diagnostics'
// category, leaving its 8 windows (all the ability/lifecycle checkers)
// invisible in the sidebar — reachable only via "all on" (DJ 2026-06-11).
// Deriving makes "defined but unlisted" unrepresentable.
const CATEGORY_ORDER = Object.keys(CATEGORY_META) as WindowCategory[];

export const DEBUG_SUITE_DEFAULT_POSITION = { x: 280, y: 110 } as const;
export const DEBUG_SUITE_DEFAULT_SIZE = { width: 700, height: 720 } as const;

interface ToggleRowProps {
  def: WindowDef;
  active: boolean;
  onClick: () => void;
}

const ToggleRow: React.FC<ToggleRowProps> = ({ def, active, onClick }) => (
  <button
    type="button"
    data-testid={`debug-suite-toggle-${def.id}`}
    onClick={onClick}
    title={def.description}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 6px',
      background: active ? 'rgba(80, 130, 200, 0.18)' : 'transparent',
      border: 'none',
      borderLeft: `2px solid ${active ? '#7af' : 'transparent'}`,
      color: active ? '#fff' : '#aaa',
      cursor: 'pointer',
      textAlign: 'left',
      fontFamily: 'monospace',
      fontSize: 11,
      fontWeight: active ? 600 : 400,
    }}
  >
    <span style={{ flex: '0 0 14px', color: active ? '#7af' : '#444' }}>
      {active ? '●' : '○'}
    </span>
    <span style={{ flex: '0 0 auto' }}>{def.icon}</span>
    <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {def.label}
    </span>
  </button>
);

interface ContentSectionProps {
  def: WindowDef;
  onCollapse: () => void;
}

const ContentSection: React.FC<ContentSectionProps> = ({ def, onCollapse }) => {
  const Content = CONTENT_BY_ID[def.id];
  if (!Content) return null;
  return (
    <div
      data-testid={`debug-suite-content-${def.id}`}
      style={{
        marginBottom: 8,
        border: '1px solid #2a2f3a',
        borderRadius: 4,
        background: 'rgba(80, 130, 200, 0.03)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 8px',
          borderBottom: '1px solid #2a2f3a',
          fontFamily: 'monospace',
          fontSize: 11,
          color: '#7af',
          fontWeight: 600,
        }}
      >
        <span>{def.icon}</span>
        <span style={{ flex: 1 }}>{def.label}</span>
        <button
          type="button"
          onClick={onCollapse}
          title="Collapse this widget"
          style={{
            background: 'transparent',
            color: '#666',
            border: '1px solid #2a2f3a',
            borderRadius: 3,
            padding: '0 6px',
            fontSize: 10,
            cursor: 'pointer',
            fontFamily: 'monospace',
          }}
        >
          ✕
        </button>
      </div>
      <div style={{ padding: '6px 8px' }}>
        <Content />
      </div>
    </div>
  );
};

export const DebugSuiteContainer: React.FC = () => {
  const {
    hubWidgets,
    windows: tools,
    setHubEnabled,
    setHubMany,
    closeAllHub,
    openAllHub,
    applyHubPreset,
    displayMode,
    setDisplayMode,
  } = useWindows();
  const toolWindowRef = useRef<ToolWindowHandle>(null);
  const [view, setView] = useState<DebugSuiteViewState>(() => {
    const saved = storage.getObjectRaw<Partial<DebugSuiteViewState>>(DEBUG_SUITE_VIEW_STORAGE_KEY);
    let scale = clampDebugSuiteScale(saved?.scale ?? 1);
    let layout: DebugSuiteLayoutId = isDebugSuiteLayoutId(saved?.layout ?? null)
      ? saved!.layout!
      : 'default';
    try {
      const fromUrl = readDebugSuiteViewFromUrl(new URL(bootHref()));
      scale = fromUrl.scale ?? scale;
      layout = fromUrl.layout ?? layout;
    } catch { /* keep saved/default view */ }
    return { scale, layout };
  });
  const [geometry, setGeometry] = useState<DebugSuiteGeometry>({
    ...DEBUG_SUITE_DEFAULT_POSITION,
    ...DEBUG_SUITE_DEFAULT_SIZE,
  });
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'unavailable'>('idle');

  useEffect(() => {
    storage.setObjectRaw(DEBUG_SUITE_VIEW_STORAGE_KEY, view);
  }, [view]);

  // A copied round URL may carry either a named layout or exact custom
  // geometry. Apply it after ToolWindow exposes its reusable control handle.
  useEffect(() => {
    const handle = toolWindowRef.current;
    if (!handle) return;
    let fromUrl: ReturnType<typeof readDebugSuiteViewFromUrl> = {};
    try { fromUrl = readDebugSuiteViewFromUrl(new URL(bootHref())); } catch { /* keep persisted ToolWindow geometry */ }
    if (fromUrl.geometry) {
      handle.setGeometry(fromUrl.geometry);
    } else if (fromUrl.layout) {
      handle.setGeometry(resolveDebugSuiteLayout(fromUrl.layout, {
        width: window.innerWidth,
        height: window.innerHeight,
      }));
    }
    const next = handle.getState();
    setGeometry({ x: next.x, y: next.y, width: next.width, height: next.height });
  }, []);

  /** Group tools by category, preserving registry order within each group. */
  const grouped = useMemo(() => {
    const out = new Map<WindowCategory, WindowDef[]>();
    for (const cat of CATEGORY_ORDER) out.set(cat, []);
    for (const t of tools) {
      const cat = t.category;
      if (!out.has(cat)) out.set(cat, []);
      out.get(cat)!.push(t);
    }
    return out;
  }, [tools]);

  const toggleWidget = useCallback((id: WindowId) => {
    setHubEnabled(id, !hubWidgets[id]?.enabled);
  }, [hubWidgets, setHubEnabled]);

  const collapseSection = useCallback((id: WindowId) => {
    setHubEnabled(id, false);
  }, [setHubEnabled]);

  const expandAll = useCallback(() => {
    openAllHub();
  }, [openAllHub]);

  const collapseAll = useCallback(() => {
    closeAllHub();
  }, [closeAllHub]);

  /** Close button leaves the embedded hub and returns to explicit floating mode. */
  const handleClose = useCallback(() => {
    setDisplayMode('floating');
  }, [setDisplayMode]);

  const switchToGranular = useCallback(() => {
    setDisplayMode('floating');
  }, [setDisplayMode]);

  const applyLayout = useCallback((layout: DebugSuiteLayoutId) => {
    const next = resolveDebugSuiteLayout(layout, {
      width: window.innerWidth,
      height: window.innerHeight,
    });
    toolWindowRef.current?.setGeometry(next);
    setGeometry(next);
    setView((prev) => ({ ...prev, layout }));
  }, []);

  const changeScale = useCallback((delta: number) => {
    setView((prev) => ({ ...prev, scale: clampDebugSuiteScale(prev.scale + delta) }));
  }, []);

  const copyConfiguration = useCallback(async () => {
    const state = toolWindowRef.current?.getState();
    const currentGeometry = state
      ? { x: state.x, y: state.y, width: state.width, height: state.height }
      : geometry;
    const selected = tools.filter((tool) => hubWidgets[tool.id]?.enabled).map((tool) => tool.id);
    const url = buildDebugSuiteShareUrl(new URL(bootHref()), selected, view, currentGeometry);
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(url);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('unavailable');
    }
  }, [geometry, hubWidgets, tools, view]);

  const expandedCount = useMemo(
    () => tools.filter((t) => hubWidgets[t.id]?.enabled).length,
    [tools, hubWidgets],
  );

  const expandedTools = useMemo(
    () => tools.filter((t) => hubWidgets[t.id]?.enabled),
    [tools, hubWidgets],
  );

  return (
    <ToolWindow
      ref={toolWindowRef}
      id="debugSuite"
      title="Netcode + Debug Hub"
      icon="📊"
      isOpen
      onClose={handleClose}
      // Wider default for the two-column layout (sidebar 180 + content 460 + chrome).
      defaultPosition={DEBUG_SUITE_DEFAULT_POSITION}
      defaultSize={DEBUG_SUITE_DEFAULT_SIZE}
      minSize={{ width: 480, height: 320 }}
      maxSize={{ width: 1200, height: 1400 }}
      collapsible
      resizable
      minimizable
      bringToFrontOnDrag
      testId="debug-suite-window"
      onPositionChange={(x, y) => setGeometry((prev) => ({ ...prev, x, y }))}
      onSizeChange={(width, height) => setGeometry((prev) => ({ ...prev, width, height }))}
    >
      <div
        data-testid="debug-suite-scaled-surface"
        data-scale={view.scale}
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: `${100 / view.scale}%`,
          height: `${100 / view.scale}%`,
          transform: `scale(${view.scale})`,
          transformOrigin: 'top left',
        }}
      >
      <div
        data-testid="debug-suite-toolbar"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 6px',
          marginBottom: 6,
          background: 'rgba(0, 0, 0, 0.25)',
          borderRadius: 3,
          fontFamily: 'monospace',
          fontSize: 10,
        }}
      >
        <span style={{ color: '#7af' }}>📊 HUB</span>
        <span style={{ color: '#888' }}>· {expandedCount}/{tools.length} on</span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => {
            // "WTF was that" moment-capture button. Dumps last 5s of
            // frame-jitter/perf to clipboard. Same as Shift+W.
            import('../../diagnostics/WtfCapture').then((m) => {
              m.captureMoment().then((r) => {
                const msg = r.snapCount > 0
                  ? `📸 Captured · ${r.snapCount} snap(s) · worst ${r.worstDivergencePx.toFixed(0)}px${r.copied ? ' · copied' : ''}`
                  : `📸 Captured · no snaps · max div ${r.worstDivergencePx.toFixed(0)}px${r.copied ? ' · copied' : ''}`;
                const toast = (window as unknown as { __errorToast?: (level: 'error' | 'warn' | 'info', m: string) => void }).__errorToast;
                if (toast) toast('info', msg);
              });
            });
          }}
          style={{ ...toolbarBtnStyle, color: '#fa5', border: '1px solid #fa5' }}
          data-testid="debug-suite-wtf-capture"
          title="📸 Capture the last 5 seconds of all diagnostic signals (frame jitter, perf). Copies to clipboard. Same as Shift+W."
        >
          📸 wtf
        </button>
        <button
          type="button"
          onClick={expandAll}
          style={toolbarBtnStyle}
          data-testid="debug-suite-expand-all"
        >
          all on
        </button>
        <button
          type="button"
          onClick={collapseAll}
          style={toolbarBtnStyle}
          data-testid="debug-suite-collapse-all"
        >
          all off
        </button>
        {displayMode.kind === 'hub' && (
          <button
            type="button"
            onClick={switchToGranular}
            style={{ ...toolbarBtnStyle, color: '#fa5' }}
            data-testid="debug-suite-switch-granular"
            title="Switch to 🔲 floating mode — close the hub; open floating cards explicitly from the launcher"
          >
            → 🔲 floating
          </button>
        )}
      </div>

      {/* Preset chips — one click swaps the embedded hub set to a task kit.
          Floating-window selection is intentionally untouched. */}
      <div
        data-testid="debug-suite-presets"
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 4,
          padding: '0 6px 6px',
          fontFamily: 'monospace',
          fontSize: 10,
        }}
      >
        <span style={{ color: '#888' }}>presets:</span>
        {DEBUG_HUB_PRESETS.map((preset) => {
          const active = preset.windows.every((id) => hubWidgets[id]?.enabled) &&
            expandedCount === preset.windows.length;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => applyHubPreset(preset.windows)}
              style={{ ...toolbarBtnStyle, ...(active ? { color: '#7af', border: '1px solid #7af' } : {}) }}
              data-testid={`debug-suite-preset-${preset.id}`}
              title={preset.title}
            >
              {preset.label}
            </button>
          );
        })}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => { void copyConfiguration(); }}
          style={toolbarBtnStyle}
          data-testid="debug-suite-copy-configuration"
          title="Copy an exact custom-tool, scale, size, and position URL"
        >
          {copyStatus === 'copied' ? '✓ copied' : copyStatus === 'unavailable' ? 'copy blocked' : 'copy set'}
        </button>
      </div>

      <div
        data-testid="debug-suite-view-controls"
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 4,
          padding: '0 6px 6px',
          fontFamily: 'monospace',
          fontSize: 10,
        }}
      >
        <span style={{ color: '#888' }}>view:</span>
        <select
          aria-label="Hub layout"
          data-testid="debug-suite-layout"
          value={view.layout}
          onChange={(event) => applyLayout(event.target.value as DebugSuiteLayoutId)}
          style={{ ...toolbarBtnStyle, background: '#11151d' }}
        >
          {DEBUG_SUITE_LAYOUT_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => changeScale(-DEBUG_SUITE_SCALE_STEP)}
          style={toolbarBtnStyle}
          data-testid="debug-suite-scale-down"
          title="Scale hub UI down"
        >
          −
        </button>
        <span data-testid="debug-suite-scale" style={{ color: '#bbb', minWidth: 34, textAlign: 'center' }}>
          {Math.round(view.scale * 100)}%
        </span>
        <button
          type="button"
          onClick={() => changeScale(DEBUG_SUITE_SCALE_STEP)}
          style={toolbarBtnStyle}
          data-testid="debug-suite-scale-up"
          title="Scale hub UI up"
        >
          +
        </button>
        <button
          type="button"
          onClick={() => toolWindowRef.current?.minimize()}
          style={{ ...toolbarBtnStyle, color: '#fa5' }}
          data-testid="debug-suite-hide"
          title="Hide to the draggable title bar; use □ to restore"
        >
          hide
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          gap: 6,
        }}
      >
        {/* LEFT: widget toggle sidebar */}
        <div
          data-testid="debug-suite-sidebar"
          style={{
            flex: '0 0 180px',
            overflowY: 'auto',
            overflowX: 'hidden',
            borderRight: '1px solid #2a2f3a',
            paddingRight: 4,
          }}
        >
          {CATEGORY_ORDER.map((cat) => {
            const inCat = grouped.get(cat) ?? [];
            if (inCat.length === 0) return null;
            const meta = CATEGORY_META[cat];
            const onCount = inCat.filter((t) => hubWidgets[t.id]?.enabled).length;
            return (
              <div
                key={cat}
                data-testid={`debug-suite-category-${cat}`}
                style={{ marginBottom: 8 }}
              >
                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 10,
                    color: '#7af',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    padding: '4px 4px 2px',
                    borderBottom: '1px solid #1a1e26',
                    marginBottom: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span>{meta.icon} {meta.label}</span>
                  <span style={{ color: '#555', marginLeft: 4 }}>
                    ({onCount}/{inCat.length})
                  </span>
                  <span style={{ flex: 1 }} />
                  <button
                    type="button"
                    onClick={() => setHubMany(inCat.map((tool) => tool.id), true)}
                    style={categoryBtnStyle}
                    data-testid={`debug-suite-category-${cat}-all-on`}
                    title={`Select all ${meta.label} tools`}
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={() => setHubMany(inCat.map((tool) => tool.id), false)}
                    style={categoryBtnStyle}
                    data-testid={`debug-suite-category-${cat}-all-off`}
                    title={`Deselect all ${meta.label} tools`}
                  >
                    −
                  </button>
                </div>
                {inCat.map((def) => (
                  <ToggleRow
                    key={def.id}
                    def={def}
                    active={!!hubWidgets[def.id]?.enabled}
                    onClick={() => toggleWidget(def.id)}
                  />
                ))}
              </div>
            );
          })}
        </div>

        {/* RIGHT: expanded widget content */}
        <div
          data-testid="debug-suite-content"
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingRight: 2,
          }}
        >
          {expandedTools.length === 0 ? (
            <div
              data-testid="debug-suite-empty"
              style={{
                color: '#666',
                fontFamily: 'monospace',
                fontSize: 11,
                padding: '12px 8px',
                textAlign: 'center',
              }}
            >
              Click a widget in the sidebar →
            </div>
          ) : (
            expandedTools.map((def) => (
              <ContentSection
                key={def.id}
                def={def}
                onCollapse={() => collapseSection(def.id)}
              />
            ))
          )}
        </div>
      </div>
      </div>
    </ToolWindow>
  );
};

const toolbarBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#aaa',
  border: '1px solid #2a2f3a',
  borderRadius: 3,
  padding: '2px 6px',
  fontSize: 10,
  cursor: 'pointer',
  fontFamily: 'monospace',
};

const categoryBtnStyle: React.CSSProperties = {
  ...toolbarBtnStyle,
  padding: '0 5px',
  color: '#7af',
};
